import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  MessageCircle,
  Users,
  ShoppingBag,
  Undo2,
  AlertTriangle,
  Zap,
  Lock,
  Hash,
  User,
  Package,
  CreditCard,
  Tag,
  Camera,
  Calendar,
  RefreshCcw,
  Phone,
  Mail,
  MapPin,
  ListOrdered,
  Pencil,
  History,
  Pause,
  CheckCircle2,
  MessageSquareWarning,
  Clock3,
  Sparkles,
  ArrowUpRight,
  BookOpen,
  Truck,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English, example-driven reference for every tab on the merchant
 * dashboard (Chats, Customers, Orders, Returns, Stuck, Automations) — what
 * each one shows, and a field-by-field breakdown of every column/section a
 * merchant will actually see on screen. Companion to StatusGuide.jsx, which
 * covers the individual status badges that appear across these tabs.
 */

// Each tab gets its own accent so a merchant can visually match this page to
// the matching tab in the dashboard at a glance.
const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25", ring: "ring-lilac" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25", ring: "ring-mint" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25", ring: "ring-peach" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10", ring: "ring-hotpink" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]", ring: "ring-ink" },
};

function FieldCard({ icon: Icon, label, desc, example, note, accent }) {
  const a = ACCENTS[accent];
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 flex-none place-items-center rounded-lg ${a.chip} ${a.chipText}`}>
          <Icon size={13} strokeWidth={2.4} />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{label}</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink/70">{desc}</p>
      {example && (
        <div className={`mt-2.5 rounded-xl ${a.soft} px-3 py-2`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Example</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-ink/80">{example}</p>
        </div>
      )}
      {note && <p className="mt-2 text-[11px] leading-relaxed text-ink/45">{note}</p>}
    </div>
  );
}

function TabActions({ actions, accent }) {
  if (!actions || actions.length === 0) return null;
  const a = ACCENTS[accent];
  return (
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {actions.map((act) => (
        <div key={act.name} className={`rounded-2xl ${a.soft} p-4`}>
          <div className="flex items-center gap-2">
            {act.icon && <act.icon size={14} className="text-ink/60" />}
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{act.name}</p>
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-ink/70">{act.meaning}</p>
        </div>
      ))}
    </div>
  );
}

function TabSection({ id, icon: Icon, accent, name, tagline, what, useFor, checkWhen, fields, actions, planNote }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
            <Icon size={20} strokeWidth={2.2} />
          </span>
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
              {tagline}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
          </div>
        </div>
        {planNote && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-ink/[0.03] px-3 py-1.5 text-[11px] font-semibold text-ink/60">
            <Lock size={11} /> {planNote}
          </span>
        )}
      </div>

      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl bg-ink/[0.03] p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Use it for</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{useFor}</p>
        </div>
        <div className="rounded-2xl bg-ink/[0.03] p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Check it when</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{checkWhen}</p>
        </div>
      </div>

      {fields && fields.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">What you'll see, field by field</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <FieldCard key={f.label} accent={accent} {...f} />
            ))}
          </div>
        </div>
      )}

      <TabActions actions={actions} accent={accent} />
    </section>
  );
}

const TABS = [
  {
    id: "chats",
    icon: MessageCircle,
    accent: "lilac",
    name: "Chats",
    tagline: "Every conversation",
    what: "Every Instagram DM conversation your bot has ever had — whether or not it turned into a sale.",
    useFor: "Getting a pulse on activity and seeing exactly where someone is in the buying flow right now — picking a product, confirming an address, waiting on payment, and so on.",
    checkWhen: "You want a general activity check, or a customer says they messaged you and you want to see the live state of that conversation.",
    fields: [
      {
        icon: User,
        label: "Customer",
        desc: "Who the bot is talking to. Tap the name to open their Instagram profile in a new tab, if their handle is on file.",
      },
      {
        icon: Package,
        label: "Cart",
        desc: "What's currently sitting in their cart at this point in the conversation — not a finished order yet, just what the bot has noted down so far.",
        example: "\"Yellow T-Shirt × 1, Red T-Shirt × 2\" means the bot has these items queued, but the customer hasn't paid (or maybe even confirmed) yet.",
      },
      {
        icon: CheckCircle2,
        label: "State",
        desc: "Exactly which step of the flow the bot and customer are on. Tap the badge for a plain-English explanation of that specific state.",
        note: (
          <>
            Full list of every possible state and what it means is on the{" "}
            <Link to="/status-guide" className="font-semibold text-hotpink hover:underline">
              status guide →
            </Link>
          </>
        ),
      },
      {
        icon: Calendar,
        label: "Started / Updated",
        desc: "Started is when this conversation first began. Updated is the last time anything happened in it — a new message from either side, or the bot moving them to a new state.",
      },
    ],
  },
  {
    id: "customers",
    icon: Users,
    accent: "mint",
    name: "Customers",
    tagline: "Every buyer, once",
    what: "Every unique person who has ever DM'd your store, deduplicated into one row each — with their saved contact details and lifetime stats.",
    useFor: "Looking someone up, correcting or updating their saved details, or opening their full order + return history in one place.",
    checkWhen: "A customer asks you to update their address or contact info, or you want their complete purchase history before replying to them.",
    fields: [
      {
        icon: User,
        label: "Customer",
        desc: "Their display name, linked to their Instagram profile when a handle is on file.",
      },
      {
        icon: Phone,
        label: "Phone",
        desc: "The number they gave the bot during checkout, if any. Shows \"—\" if it was never collected.",
      },
      {
        icon: Mail,
        label: "Email",
        desc: "Used for order confirmation and return decision emails, when collected.",
      },
      {
        icon: MapPin,
        label: "Address",
        desc: "Their most recent delivery address on file. If they order again with a new address, this updates to the latest one — it isn't a history of every address they've ever used.",
      },
      {
        icon: ListOrdered,
        label: "Orders / Spent",
        desc: "Lifetime counts — every order they've ever placed and the total they've paid you, all-time. These never reset.",
      },
      {
        icon: History,
        label: "History (action)",
        desc: "Opens a full timeline of this customer's orders and returns — useful for context before you reply to them, especially for a return or a complaint.",
      },
      {
        icon: Pencil,
        label: "Edit (action)",
        desc: "Lets you manually correct their name, phone, email, or address — handy if a customer typed something wrong at checkout and asks you to fix it.",
      },
    ],
  },
  {
    id: "orders",
    icon: ShoppingBag,
    accent: "peach",
    name: "Orders",
    tagline: "Every order placed",
    what: "Every order placed through the bot. Free merchants see items and payment status; Starter, Growth & Pro merchants can additionally mark an order Shipped, which also determines when that order's return window starts; Growth & Pro merchants additionally see which discount, if any, applied.",
    useFor: "Confirming or rejecting manually-reported payments (UPI/bank transfer), and logging a return directly against a paid order.",
    checkWhen: "Your Paid orders stat card moves, or you want to check whether a specific order has actually been paid before shipping it.",
    fields: [
      {
        icon: Hash,
        label: "Order #",
        desc: "A short reference code for the order — e.g. CDM-000140. Use this when a customer quotes it to you, or when you search for an order.",
      },
      {
        icon: User,
        label: "Customer",
        desc: "Who placed the order, linked to their Instagram profile when their handle is on file.",
      },
      {
        icon: Package,
        label: "Items",
        desc: "Every product in the order, one per line — product name, size if it has one, and quantity.",
        example: "\"Red T-Shirt · M × 9\" means 9 units of the Red T-Shirt in size M, in this single order.",
      },
      {
        icon: CreditCard,
        label: "Amount",
        desc: "The final amount the customer paid. If a discount or shipping fee applied, a \"View breakup\" link appears underneath — tap it to see the subtotal, discount, and shipping broken out separately. If neither applied, it just shows the plain total with no link, since there's nothing extra to break down.",
        example: "Amount ₹630, View breakup opens: Subtotal ₹700 → Discount (SHOP20) −₹70 → Total ₹630.",
      },
      {
        icon: Tag,
        label: "Discount",
        desc: "Growth & Pro only. Shows a purple badge with the exact coupon code the customer typed (e.g. WELCOME100), or a green \"Auto\" badge when your best-value automatic discount applied instead — with the percentage or \"free shipping\" shown right on the badge. A dash means no discount applied to that order.",
        note: "If you downgrade off Growth/Pro later, this badge stops appearing on new orders — but old orders keep their discount details inside \"View breakup,\" so your history stays accurate.",
      },
      {
        icon: Camera,
        label: "Screenshot",
        desc: "Only appears for orders paid manually — a UPI QR code, a UPI ID, or a direct bank transfer — where CheckoutDM has no automatic way to confirm the payment the way it can with a Razorpay payment link. The customer sends a screenshot of their payment as proof, right in the DM. You check it against your bank account, then tap Confirm (order moves to Paid, customer gets a confirmation DM) or Reject (customer gets a rejection DM, and you're expected to sort out the dispute with them directly).",
        example: "A customer scans your UPI QR code, pays ₹599, and DMs a screenshot of the successful transaction. It lands here — you check your bank app, spot the ₹599, and tap Confirm.",
        note: "The screenshot image is auto-deleted from CheckoutDM's own storage after 90 days to keep storage costs down — but it stays visible forever in your actual Instagram DM thread with that customer, since that's Instagram's own chat history, not CheckoutDM's.",
      },
      {
        icon: CheckCircle2,
        label: "Status",
        desc: "Where the order currently stands — Pending Payment, Paid, Returned, and so on. Tap the badge for what that specific status means.",
        note: (
          <>
            Full list on the{" "}
            <Link to="/status-guide" className="font-semibold text-hotpink hover:underline">
              status guide →
            </Link>
          </>
        ),
      },
      {
        icon: Calendar,
        label: "Created",
        desc: "The date the order was placed.",
      },
      {
        icon: Truck,
        label: "Shipped",
        desc: "Shows \"Not shipped yet\" with a \"Mark as Shipped\" button until you tap it, then switches to the date you shipped the order. This date matters beyond your own records — it's what starts the customer's return-window countdown (plus a short transit buffer) instead of the order date. Until an order is marked Shipped, the customer can ask to cancel it any time — no countdown is running yet.",
      },
      {
        icon: RefreshCcw,
        label: "Return",
        desc: "For Paid orders with no return on file yet, a \"Log a return\" link lets you record one yourself — useful when you already know an item needs to go back (damaged, wrong size sent) without waiting for the customer to formally request it. Once a return exists, this shows its status badge instead (Returned, Partially Returned, Requested, or Rejected).",
        note: "Logging a return yourself here isn't limited by the return window — that check only applies to a customer requesting one through the bot.",
      },
    ],
  },
  {
    id: "returns",
    icon: Undo2,
    accent: "hotpink",
    name: "Returns",
    tagline: "Every return request",
    what: "Every return that's been filed against an order — whether the customer requested it themselves, or you logged it directly from the Orders tab. A customer can only request one through the bot if the order is still within its return window; logging one yourself from Orders isn't limited by that window.",
    useFor: "Deciding on open return requests. Approving automatically restocks the returned item(s) and notifies the customer; rejecting sends a DM explaining why, along with your return policy.",
    checkWhen: "A return shows a Requested status and needs your decision, or you want a record of past return decisions for a customer.",
    fields: [
      {
        icon: Hash,
        label: "Order #",
        desc: "Which order this return belongs to — matches the Order # you'd see in the Orders tab.",
      },
      {
        icon: User,
        label: "Customer",
        desc: "Who's requesting (or received) the return.",
      },
      {
        icon: MessageSquareWarning,
        label: "Reason",
        desc: "Why the customer wants to return it, in their own words (or your note, if you logged the return yourself). If only some items from a multi-item order are being returned, a \"View items\" link shows exactly which ones.",
      },
      {
        icon: CheckCircle2,
        label: "Status",
        desc: "Requested (needs your decision), Approved, or Rejected. A separate \"Partial return\" tag appears when the return only covers some items from the order, not the whole thing.",
      },
      {
        icon: Calendar,
        label: "Requested",
        desc: "The date the return was filed.",
      },
    ],
  },
  {
    id: "stuck",
    icon: AlertTriangle,
    accent: "hotpink",
    name: "Stuck",
    tagline: "Needs you, specifically",
    what: "Chats that need YOU — not a general activity feed. A conversation lands here when the bot got stuck and handed it over, a cart's been sitting untouched, a chosen product got deleted, there's been no reply in 7+ days, or the customer explicitly asked for a human.",
    useFor: "Your daily \"what needs a reply from me\" list. If this tab is empty, nothing is currently waiting on you.",
    checkWhen: "Every day, ideally — this is the one tab designed to be checked regularly rather than searched.",
    fields: [
      {
        icon: User,
        label: "Customer",
        desc: "Who's waiting on you, linked to their Instagram profile when available.",
      },
      {
        icon: CheckCircle2,
        label: "State",
        desc: "The bot conversation state this customer is currently frozen in.",
      },
      {
        icon: Clock3,
        label: "Retries",
        desc: "How many times the bot already tried (and failed) to move this conversation forward on its own before giving up and flagging it to you.",
      },
      {
        icon: MessageSquareWarning,
        label: "Stuck on",
        desc: "Why this specific row needs you. A pink \"🙋 Wants a human\" tag means the customer explicitly asked to talk to a person, with their message shown underneath. Otherwise it names the reason — e.g. abandoned cart — and, on Growth/Pro, shows what the customer actually said when asked why they didn't complete checkout.",
        example: "\"💬 Asked about a discount\" under an abandoned cart tells you the customer stalled because they wanted a better price — useful context before you reach out.",
        note: "On Free/Starter, the abandonment reason is replaced with an \"Upgrade to see why\" link instead of the actual reason.",
      },
      {
        icon: Calendar,
        label: "Last update",
        desc: "The last time anything happened in this conversation — helps you judge how urgent (or stale) it's gotten.",
      },
    ],
    actions: [
      {
        name: "Pause Bot",
        icon: Pause,
        meaning: "Stops the bot from auto-replying to that one customer for however long you set — a preset or a custom number of minutes — so you can jump into Instagram and DM them yourself without the bot talking over you. It only affects that one customer; everyone else's conversations keep running normally. It resumes automatically when the timer runs out, or you can hit Resume Bot early once you're done. Pausing does not clear the row from Stuck — that's what Resolve is for.",
      },
      {
        name: "Resolve",
        icon: CheckCircle2,
        meaning: "Marks the conversation as handled and removes it from your Stuck list. Use it once you've sorted things out with the customer, whether or not you used Pause Bot to do it. It never happens automatically — a row stays in Stuck until you resolve it yourself or the bot moves the conversation forward on its own.",
      },
    ],
  },
  {
    id: "automations",
    icon: Zap,
    accent: "ink",
    name: "Automations",
    tagline: "Comments → checkout DMs",
    what: "Rules that turn a comment on your Instagram posts or reels into a private checkout DM automatically, based on keywords you choose.",
    useFor: "Turning post engagement straight into DM conversations — no manually replying to every \"price?\" comment.",
    checkWhen: "You're about to launch a post and want a specific comment keyword to auto-trigger a checkout flow for a specific product.",
    planNote: "Creator and above",
  },
];

export default function TabsGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Dashboard Tabs Guide",
    description: "A field-by-field walkthrough of the Chats, Customers, Orders, Returns, Stuck, and Automations tabs.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/tabs-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Dashboard Tabs Guide", item: "https://checkoutdm.com/tabs-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Dashboard Tabs Guide | Checkout DM"
        description="A field-by-field walkthrough of the Chats, Customers, Orders, Returns, Stuck, and Automations tabs."
        path="/tabs-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          What each dashboard tab means
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          A field-by-field walkthrough of Chats, Customers, Orders, Returns,
          Stuck, and Automations — what every column, badge, and button
          actually does, with real examples. Chats and Stuck sound similar
          but serve very different purposes, so start there if you're new.
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/status-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What each status badge means →
          </Link>
          <Link to="/analytics-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <Sparkles size={13} /> What each Analytics number means →
          </Link>
        </p>

        {/* Quick nav */}
        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const a = ACCENTS[t.accent];
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full ${a.chip} ${a.chipText} px-3.5 py-1.5 text-xs font-bold`}
              >
                <t.icon size={12} /> {t.name}
              </a>
            );
          })}
        </div>

        <div className="mt-8 space-y-6">
          {TABS.map((t) => (
            <TabSection key={t.id} {...t} />
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
            <p className="mt-1 max-w-md text-sm text-ink/70">
              If something on your dashboard still doesn't make sense after reading this, reach out and we'll add it here for the next person too.
            </p>
          </div>
          <a
            href="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            See plans <ArrowUpRight size={14} />
          </a>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
